import {Menu, MenuButton, MenuItem, MenuItems} from "@headlessui/react";
import {useState, useEffect} from "react";
import {BsFilter, BsSearch} from "react-icons/bs";
import {TableData} from "../../../components/TableData";
import {StatusCard} from "../../../components/StatusCard";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {EmptyState} from "../../../components/EmptyState";
import axios from "axios";
import {useQuery} from "react-query";
import {getStatusType} from "../../../utils/status";
import {DateRange, DateRangePicker, RangeKeyDict} from "react-date-range";
import en from "date-fns/locale/en-US";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import moment from "moment";

const TABLE_HEAD = [
  "",
  "Created at",
  "Sender",
  "Order ID",
  "Pickup Address",
  "City",
  "Pickup Date",
  "Phone number",
  "Delivery Type",
  "Status",
];

const DELIVERY_TYPE_OPTIONS = [
  {label: "All", value: ""},
  {label: "Standard", value: "STANDARD"},
  {label: "Express", value: "EXPRESS"},
  {label: "Same Day", value: "SAME_DAY"},
];

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const toAdjustedISO = (date: Date): string => {
  const adjustedDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 60000
  );
  adjustedDate.setHours(0, 0, 0, 0);
  // Add one hour
  adjustedDate.setHours(adjustedDate.getHours() + 1);
  return adjustedDate.toISOString().slice(0, 19) + "Z";
};

const toAdjustedISOEnd = (date: Date): string => {
  const adjustedDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 60000
  );
  adjustedDate.setHours(23, 59, 59, 0);
  adjustedDate.setHours(adjustedDate.getHours() + 1);
  return adjustedDate.toISOString().slice(0, 19) + "Z";
};

export const UnassignedPickupsTable = ({
  selectedPickups,
  setSelectedPickups,
  hasSubmitted,
  setHasSubmitted,
}: {
  selectedPickups: any[];
  setSelectedPickups: React.Dispatch<React.SetStateAction<any[]>>;
  setHasSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  hasSubmitted: boolean;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const Baseurl = process.env.REACT_APP_BASEURL;
  const token = JSON.parse(localStorage.getItem("eftd-admin-token") || "{}")
    ?.data?.data?.token;

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLabel, setSelectedLabel] = useState("Filter");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [deliveryTypeFilter, setDeliveryTypeFilter] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState<any>({
    startDate: null,
    endDate: null,
    key: "selection",
  });

  // Get current page from URL or default to 1
  const queryParams = new URLSearchParams(location.search);
  const currentPage = Number(queryParams.get("page")) || 1;

  const config = {
    headers: {Authorization: `Bearer ${token}`},
  };

  // Debounce search input
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchQuery]);

  const handleSelectDelivery = (value: any) => {
    setSelectedPickups((prev) => {
      if (prev.some((pickup) => pickup.id === value.id)) {
        return prev.filter((v) => v.id !== value.id);
      } else {
        return [...prev, value];
      }
    });
  };

  const getPickups = async () => {
    const params = new URLSearchParams({
      limit: limit.toString(),
      page: currentPage.toString(),
    });

    if (debouncedSearchQuery) {
      params.append("query", encodeURIComponent(debouncedSearchQuery));
    }

    if (deliveryTypeFilter) {
      params.append("filter", encodeURIComponent(deliveryTypeFilter));
    }

    if (dateRange.startDate && dateRange.endDate) {
      params.append("from", toAdjustedISO(dateRange.startDate));
      params.append("to", toAdjustedISOEnd(dateRange.endDate));
    }

    let url = `${Baseurl}/api/admin/orders/pickups?${params.toString()}&filter=status:UNASSIGNED&sort=pickupDate:DESC`;
    const response = await axios.get(url, config);
    setTotalPages(Math.ceil(response?.data?.data?.meta?.total / limit));
    return response?.data?.data?.items;
  };

  const {
    data: pickupsData,
    isLoading: isLoadingPickupData,
    refetch,
    isRefetching: isRefetchingPickupData,
  } = useQuery(
    [
      "UNASSIGNED_PICKUPS",
      currentPage,
      limit,
      debouncedSearchQuery,
      deliveryTypeFilter,
    ],
    getPickups,
    {
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    refetch();
  }, [
    debouncedSearchQuery,
    deliveryTypeFilter,
    currentPage,
    limit,
    dateRange,
    refetch,
  ]);

  useEffect(() => {
    if (hasSubmitted) {
      refetch();
      setHasSubmitted(false);
    }
  }, [hasSubmitted, refetch, setHasSubmitted]);

  const handlePageChange = (newPage: number) => {
    navigate(`?page=${newPage}`);
  };

  const DateRangePicker = DateRange as any;

  const handleDeliveryTypeFilter = (value: string, label: string) => {
    setDeliveryTypeFilter(value);
    setSelectedLabel(label === "All" ? "Filter" : label);
    handlePageChange(1); // Reset to first page when filter changes
  };

  const containerStyle = {
    width: "100%",
    borderRadius: "8px",
    border: "1px solid #e4e4e7",
  };

  const tableRowStyle = {
    borderBottom: "1px solid #eaecf0",
  };

  return (
    <div style={containerStyle}>
      <div className="flex items-center justify-between !px-6 !py-4">
        <h3 className="!text-xl !font-bold text-gray-700">
          Unassigned Pickups
        </h3>
        <div className="flex gap-3">
          {/* Search */}
          <div className="relative !h-12 !bg-gray-50 !border !rounded-[10px] !w-[428px] !border-gray-300">
            <input
              type="text"
              placeholder="Search your orders"
              className="w-full h-full focus:!outline-none !pl-14 placeholder:!text-black/30 !text-sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="!absolute !top-3.5 !left-6">
              <BsSearch />
            </span>
          </div>

          {/* Date Picker */}
          <div className="relative flex gap-1">
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="flex gap-1 items-center py-[13px] !px-[9px] !bg-greyscale-50 !rounded-[6px] border border-gray-200"
            >
              <span>
                <svg
                  width="20"
                  height="21"
                  viewBox="0 0 20 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.5 6.95801H2.5C2.15833 6.95801 1.875 6.67467 1.875 6.33301C1.875 5.99134 2.15833 5.70801 2.5 5.70801H17.5C17.8417 5.70801 18.125 5.99134 18.125 6.33301C18.125 6.67467 17.8417 6.95801 17.5 6.95801Z"
                    fill="#A4ACB9"
                  />
                  <path
                    d="M15 11.125H5C4.65833 11.125 4.375 10.8417 4.375 10.5C4.375 10.1583 4.65833 9.875 5 9.875H15C15.3417 9.875 15.625 10.1583 15.625 10.5C15.625 10.8417 15.3417 11.125 15 11.125Z"
                    fill="#A4ACB9"
                  />
                  <path
                    d="M11.6668 15.292H8.3335C7.99183 15.292 7.7085 15.0087 7.7085 14.667C7.7085 14.3253 7.99183 14.042 8.3335 14.042H11.6668C12.0085 14.042 12.2918 14.3253 12.2918 14.667C12.2918 15.0087 12.0085 15.292 11.6668 15.292Z"
                    fill="#A4ACB9"
                  />
                </svg>
              </span>
              <span className="text-greyscale-400 text-xs md:text-sm font-medium md:pr-4">
                {dateRange.startDate && dateRange.endDate
                  ? `${formatDate(
                      dateRange.startDate.toString()
                    )} - ${formatDate(dateRange.endDate.toString())}`
                  : "Select Date Range"}
              </span>
              <span className="hidden md:block">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.05994 6.68262L7.94994 11.5726C8.52744 12.1501 9.47244 12.1501 10.0499 11.5726L14.9399 6.68262"
                    stroke="#BFBFBF"
                    strokeWidth="1.5"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </button>
            {dateRange.startDate && (
              <button
                onClick={() => {
                  setDateRange({
                    startDate: null,
                    endDate: null,
                    key: "selection",
                  });
                  setShowDatePicker(false);
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.75729 17.2426L11.9999 12M17.2426 6.75736L11.9999 12M11.9999 12L6.75729 6.75736M11.9999 12L17.2426 17.2426"
                    stroke="black"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
            {showDatePicker && (
              <div className="absolute z-50 bottom-full right-0 mt-2 bg-white shadow-md rounded-md">
                <DateRangePicker
                  locale={en}
                  editableDateInputs={false}
                  onChange={(item: RangeKeyDict) =>
                    setDateRange(item.selection)
                  }
                  moveRangeOnFirstSelection={false}
                  ranges={[dateRange]}
                  rangeColors={["#3b82f6"]}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {isLoadingPickupData || isRefetchingPickupData ? (
        <div style={{padding: "56px 0"}}>
          <p>Loading...</p>
        </div>
      ) : pickupsData?.length > 0 ? (
        <>
          <div className="!overflow-auto">
            <table className="!w-full !min-w-max !table-auto !text-left">
              <thead>
                <tr className="!border-b !border-b-[#eaecf0] !bg-[#f9fafb]">
                  {TABLE_HEAD.map((head) => (
                    <th
                      key={head}
                      className="!text-xs !font-medium !text-[#667085] !p-3"
                    >
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pickupsData.map((order: any) => {
                  const status = getStatusType(order.status);
                  const formattedDate = new Date(
                    order.pickupDate
                  ).toLocaleDateString("en-US", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  });

                  return (
                    <tr
                      key={order.id}
                      style={tableRowStyle}
                      className="border-b border-gray-100 hover:bg-gray-100 transition-colors"
                      onClick={() => handleSelectDelivery(order)}
                    >
                      <td style={{padding: "24px 16px"}}>
                        <input
                          type="checkbox"
                          id={order.id}
                          name="user"
                          value={order.id}
                          className="w-4 h-4 accent-orange-500 border-gray-200"
                          checked={selectedPickups.some(
                            (pickup) => pickup.id === order.id
                          )}
                          onClick={() => {
                            handleSelectDelivery(order);
                          }}
                          onClickCapture={() => handleSelectDelivery(order)}
                        />
                      </td>
                      <TableData
                        content={
                          order.createdAt ? (
                            <>
                              {moment(order.createdAt).format("DD MMM, YYYY")}
                              <br />
                              {moment(order.createdAt).format("hh:mmA")}
                            </>
                          ) : (
                            "N/A"
                          )
                        }
                      />

                      <TableData content={order.senderName} />
                      <TableData content={order.orderId} />
                      <td className="!text-[13px] !font-medium !text-gray-600 !py-5 !px-3 text-wrap max-w-[200px]">
                        {`${order.address}`}
                      </td>
                      <TableData
                        content={
                          order.city && order.city !== "" ? order.city : "N/A"
                        }
                      />
                      <TableData content={formattedDate} />
                      <TableData content={order.phone} />
                      <TableData content={order.deliveryType} />
                      <td style={{padding: "20px 12px"}}>
                        <StatusCard title={status.title} type={status.type} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <div className="flex items-center gap-6">
              <p className="text-sm text-gray-500">
                Showing page {currentPage} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 text-sm font-medium rounded-md border ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 text-sm font-medium rounded-md border ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-500">Items per page:</p>
              <select
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  handlePageChange(1);
                }}
                className="border border-gray-500 rounded py-1 px-2 text-sm text-gray-500"
                value={limit}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="30">30</option>
                <option value="40">40</option>
                <option value="50">50</option>
              </select>
            </div>
          </div>
        </>
      ) : (
        <div style={{padding: "56px 0"}}>
          <EmptyState type="order" text="No Orders Found" />
        </div>
      )}
    </div>
  );
};
